import {
	Dtos,
	type FinancialPeriod,
	FinancialPeriodPreparationMethod,
	type IncomeStatementId,
} from "@inrev/common";
import { DateTime } from "luxon";
import {
	buildPeriodRecord,
	type FinancialViewCell,
	type FinancialViewRow,
	type PeriodViewModel,
	type PpChangeColorMode,
	type StatementMatrixViewModel,
} from "../../ui/FinancialDataGrid";
import { IncomeStatementWithView } from "../types";
import { FinancialPeriodDto } from "../utils";
import { incomeStatementTypeLabel } from "./financialStatementSlots";

type IncomeStatement = Dtos.Admin.IncomeStatement.Get.Response;

const periodTypeLabel = (incomeStatement: IncomeStatement): string => {
	const type = incomeStatementTypeLabel(incomeStatement.periodType);
	const method = FinancialPeriodPreparationMethod.displayName(incomeStatement.preparationMethod);
	return `${type} | ${method}`;
};

type IncomeStatementView = FinancialPeriod.IncomeStatementView;

type IncomeStatementEditableFieldKey = Extract<
	keyof IncomeStatementView,
	"cogs" | "operatingExpenses" | "otherIncome" | "otherExpenses" | "taxes"
>;

type IncomeStatementRollupFieldKey = Extract<
	keyof IncomeStatementView,
	"grossProfit" | "operatingProfit" | "netProfit"
>;

type IncomeStatementMemoFieldKey = Extract<
	keyof IncomeStatementView,
	"depreciationAndAmortization" | "distributions" | "interestExpense"
>;

type EditableMainRowSpec = {
	id: IncomeStatementEditableFieldKey;
	label: string;
	kind: "editable";
	accessor: (view: IncomeStatementView) => FinancialPeriod.LeafView;
};

type SubtotalMainRowSpec = {
	id: IncomeStatementRollupFieldKey;
	label: string;
	kind: "subtotal";
	accessor: (view: IncomeStatementView) => FinancialPeriod.RollupView;
};

type MainRowSpec = EditableMainRowSpec | SubtotalMainRowSpec;

type MemoRowSpec = {
	id: IncomeStatementMemoFieldKey;
	label: string;
	accessor: (view: IncomeStatementView) => FinancialPeriod.LeafView;
};

type LeafAccessor = (
	view: IncomeStatementView,
) => FinancialPeriod.LeafView | FinancialPeriod.RollupView;

const leafCell = (
	leaf: FinancialPeriod.LeafView | FinancialPeriod.RollupView,
): FinancialViewCell => {
	const adj = leaf.adjusted;
	return {
		given: leaf.given ?? null,
		adjustment: typeof adj === "object" ? adj : null,
		allowed: leaf.allowed ?? null,
	};
};

const emptyCell = (): FinancialViewCell => ({ given: null, adjustment: null, allowed: null });

const buildEmptyCells = (
	periods: PeriodViewModel<IncomeStatementId>[],
): Record<IncomeStatementId, FinancialViewCell> =>
	buildPeriodRecord(
		periods.map((p) => p.id),
		() => emptyCell(),
	);

const buildLeafRow = (
	id: string,
	label: string,
	kind: FinancialViewRow["kind"],
	periods: PeriodViewModel<IncomeStatementId>[],
	viewByPeriod: Map<IncomeStatementId, FinancialPeriod.IncomeStatementView>,
	accessor: LeafAccessor,
	ppChangeColorMode?: PpChangeColorMode,
): FinancialViewRow<IncomeStatementId> => {
	const cells = buildPeriodRecord(
		periods.map((p) => p.id),
		(periodId) => {
			const view = viewByPeriod.get(periodId);
			return view ? leafCell(accessor(view)) : emptyCell();
		},
	);
	return { id, level: 1, kind, label, cells, ppChangeColorMode };
};

const buildRevenueRows = (
	periods: PeriodViewModel<IncomeStatementId>[],
	viewByPeriod: Map<IncomeStatementId, FinancialPeriod.IncomeStatementView>,
): FinancialViewRow<IncomeStatementId> => {
	const labelToValuesByPeriod = new Map<
		string,
		Map<
			IncomeStatementId,
			{
				given: number | null;
				adjusted: FinancialPeriod.AdjustedLeaf | null;
				allowed: number | null;
			}
		>
	>();

	for (const period of periods) {
		const view = viewByPeriod.get(period.id);
		if (!view) continue;
		for (const item of view.revenue.lineItems) {
			let m = labelToValuesByPeriod.get(item.label);
			if (!m) {
				m = new Map();
				labelToValuesByPeriod.set(item.label, m);
			}
			m.set(period.id, {
				given: item.given ?? null,
				adjusted: item.adjusted ?? null,
				allowed: item.allowed ?? null,
			});
		}
	}

	const leafRows: FinancialViewRow<IncomeStatementId>[] = [];
	for (const [label, valuesByPeriod] of labelToValuesByPeriod) {
		const cells = buildPeriodRecord(
			periods.map((p) => p.id),
			(periodId) => {
				const vals = valuesByPeriod.get(periodId) ?? {
					given: null,
					adjusted: null,
					allowed: null,
				};
				return { given: vals.given, adjustment: vals.adjusted, allowed: vals.allowed };
			},
		);
		leafRows.push({
			id: `revenue::${label}`,
			level: 2,
			kind: "data",
			label,
			cells,
		});
	}

	const revenueCells = buildPeriodRecord(
		periods.map((p) => p.id),
		(periodId) => {
			const view = viewByPeriod.get(periodId);
			return view ? leafCell(view.revenue.total) : emptyCell();
		},
	);

	return {
		id: "revenue",
		level: 1,
		kind: "category",
		label: "Revenue",
		cells: revenueCells,
		hideValuesWhenExpanded: true,
		allowsAddedLineItems: true,
		defaultCollapsed: true,
		children: [
			...leafRows,
			{
				id: "revenue::__subtotal",
				level: 2,
				kind: "subtotal",
				label: "Total Revenue",
				cells: revenueCells,
				indentOffset: -1,
			},
		],
	};
};

/**
 * Rows whose period-over-period change has no favorable/unfavorable direction, so the badge is rendered
 * in neutral gray rather than green/red.
 */
const NEUTRAL_PP_CHANGE_ROW_IDS = new Set<string>([
	"cogs",
	"operatingExpenses",
	"otherIncome",
	"otherExpenses",
	"taxes",
]);

const MAIN_ROWS: MainRowSpec[] = [
	{ id: "cogs", label: "Cost of Goods Sold (COGS)", kind: "editable", accessor: (v) => v.cogs },
	{ id: "grossProfit", label: "Gross Profit", kind: "subtotal", accessor: (v) => v.grossProfit },
	{
		id: "operatingExpenses",
		label: "Operating Expenses (G&A)",
		kind: "editable",
		accessor: (v) => v.operatingExpenses,
	},
	{
		id: "operatingProfit",
		label: "Operating Profit",
		kind: "subtotal",
		accessor: (v) => v.operatingProfit,
	},
	{ id: "otherIncome", label: "Other Income", kind: "editable", accessor: (v) => v.otherIncome },
	{
		id: "otherExpenses",
		label: "Other Expenses",
		kind: "editable",
		accessor: (v) => v.otherExpenses,
	},
	{ id: "taxes", label: "Taxes", kind: "editable", accessor: (v) => v.taxes },
	{ id: "netProfit", label: "Net Profit", kind: "subtotal", accessor: (v) => v.netProfit },
];

const MEMO_ROWS: MemoRowSpec[] = [
	{ id: "interestExpense", label: "Interest Expense", accessor: (v) => v.interestExpense },
	{
		id: "depreciationAndAmortization",
		label: "Depreciation & Amortization",
		accessor: (v) => v.depreciationAndAmortization,
	},
	{ id: "distributions", label: "Distributions", accessor: (v) => v.distributions },
];

type BuildIncomeStatementViewModelOptions = {
	isAdmin: boolean;
};

export function buildIncomeStatementViewModel(
	incomeStatements: IncomeStatementWithView[],
	financialPeriods: FinancialPeriodDto[],
	{ isAdmin }: BuildIncomeStatementViewModelOptions,
): StatementMatrixViewModel<IncomeStatementId> {
	const periods: PeriodViewModel<IncomeStatementId>[] = incomeStatements.map((is) => ({
		id: is.id,
		label: DateTime.fromISO(is.periodEndDate).toFormat("MM/dd/yyyy"),
		periodTypeLabel: periodTypeLabel(is),
		periodEndDate: is.periodEndDate,
		periodType: is.periodType,
		preparationMethod: is.preparationMethod,
		sourceStatementDate: is.sourceStatementDate,
		sourceDataSource: is.sourceDataSource,
		reviewedAt: is.underwriterReviewedAt,
		fileId: is.fileId,
		isActive: Boolean(financialPeriods.find((fp) => fp.activeIncomeStatementId === is.id)),
	}));

	if (periods.length === 0) return { periods, rows: [] };

	const viewByPeriod = new Map<IncomeStatementId, FinancialPeriod.IncomeStatementView>();
	const statementByPeriod = new Map<IncomeStatementId, IncomeStatement>();
	for (const is of incomeStatements) {
		statementByPeriod.set(is.id, is);
		viewByPeriod.set(is.id, is.view);
	}

	// `given` and `allowed` are computed independently so underwriters can tell whether a
	// statement only became profitable after adjustments.
	const booleanCells = buildPeriodRecord(
		periods.map((p) => p.id),
		(periodId) => {
			const statement = statementByPeriod.get(periodId);
			const givenNetProfit = viewByPeriod.get(periodId)?.netProfit.given;
			return {
				given: givenNetProfit == null ? null : givenNetProfit > 0,
				adjustment: null,
				allowed: statement?.profitable ?? null,
			};
		},
	);

	const rows: FinancialViewRow<IncomeStatementId>[] = [
		buildRevenueRows(periods, viewByPeriod),
		...MAIN_ROWS.map((spec) =>
			buildLeafRow(
				spec.id,
				spec.label,
				spec.kind,
				periods,
				viewByPeriod,
				spec.accessor,
				NEUTRAL_PP_CHANGE_ROW_IDS.has(spec.id) ? "neutral" : undefined,
			),
		),
		{
			id: "profitable",
			level: 1,
			kind: "boolean",
			label: "Profitable Y/N",
			cells: buildEmptyCells(periods),
			booleanCells,
		},
		{ id: "spacer::1", level: 1, kind: "spacer", label: "", cells: buildEmptyCells(periods) },
		{
			id: "memo::label",
			level: 1,
			kind: "label",
			label: "Call Outs",
			cells: buildEmptyCells(periods),
		},
		...MEMO_ROWS.filter((spec) => isAdmin || spec.id !== "distributions").map((spec) =>
			buildLeafRow(spec.id, spec.label, "memo", periods, viewByPeriod, spec.accessor),
		),
	];

	return { periods, rows };
}
